import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Eye, EyeOff, BadgeCheck, Check, X, Loader2, RefreshCw, Scale, Wallet, AlertTriangle, Bell } from "lucide-react";
import { fmtMoney } from "@/lib/payments";
import { ALL_STATUSES, W_STATUS, payoutType, maskedLine, fmtDate } from "@/lib/wallet";
import { StatusChip, WithdrawalTimeline } from "@/components/wallet/StatusChip";
import { WithdrawalPaymentGuide } from "@/components/admin/WithdrawalPaymentGuide"; // lot 759 — guide de versement
import { useGuard } from "@/lib/guards";

const ROLE = { preacher: "Prédicateur", artist: "Artiste", influencer: "Influenceur", super_admin: "Super admin" };
// Identity (KYC Yalola) — withdrawals depend on identity_status only, never on the creator-role kyc_status.
const KYC = { verified: ["Identité vérifiée", "#D1FAE5", "#065F46"], pending_review: ["Identité en revue", "#FEF3C7", "#92400E"], in_progress: ["Identité en cours", "#DBEAFE", "#1E40AF"],
  requires_resubmission: ["Nouvelle pièce demandée", "#FFEDD5", "#9A3412"], rejected: ["Identité refusée", "#FEE2E2", "#991B1B"], expired: ["Identité expirée", "#FEE2E2", "#991B1B"], not_started: ["Identité non vérifiée", "#F3F4F6", "#374151"] };
export const identityOf = (u, fallback) => {
  const st = u?.identity_status || fallback || "not_started";
  return st === "verified" && u?.identity_expires_at && u.identity_expires_at <= new Date().toISOString() ? "expired" : st;
};
const ACTION_META = {
  review: { label: "Examiner", hint: "Passe la demande « En examen » (le membre peut encore l'annuler).", variant: "outline" },
  approve: { label: "Approuver", hint: "Déclenche le délai de traitement ; le versement reste une action manuelle.", variant: "primary" },
  process: { label: "Passer en versement", hint: "Le virement est en cours d'exécution (normalement automatique à la date prévue).", variant: "outline" },
  pay: { label: "Marquer payé", hint: "Le virement a été effectué : la référence est obligatoire.", variant: "primary", needsRef: true },
  reject: { label: "Rejeter", hint: "Le montant bloqué est recrédité au membre. Motif obligatoire (envoyé au membre).", variant: "danger", needsNote: true },
  fail: { label: "Versement échoué", hint: "Le virement n'a pas abouti : montant recrédité. Motif obligatoire.", variant: "danger", needsNote: true },
};

export const KycChip = ({ status, testId }) => {
  const [l, bg, c] = KYC[status] || KYC.not_started;
  return <span className="chip text-[10px]" style={{ background: bg, color: c }} data-testid={testId} data-status={status}>{l}</span>;
};

const MemberCell = ({ u }) => (
  <div className="min-w-0">
    <div className="font-semibold text-sm truncate">{u?.name || "—"} <span className="chip text-[10px]">{ROLE[u?.role] || u?.role}</span></div>
    <div className="text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>{u?.email || u?.id}</div>
  </div>
);

const PayoutCell = ({ snap }) => {
  const pt = payoutType(snap?.type);
  return (
    <div className="text-xs min-w-0">
      <div className="font-medium flex items-center gap-1 flex-wrap">{snap?.label || pt.fr}
        {snap?.verified_by_admin && <span className="chip text-[10px]" style={{ background: "#D1FAE5", color: "#065F46" }}><BadgeCheck size={10} className="inline mr-0.5" />Vérifié</span>}
      </div>
      <div className="truncate max-w-[220px]" style={{ color: "var(--yalola-text-muted)" }} title={maskedLine(snap)}>{pt.fr} · {maskedLine(snap) || "—"}</div>
    </div>
  );
};

// Decrypted payout details (audited on the backend) — shown on explicit click only.
const RevealDetails = ({ methodId, canManage, testId = "payout-reveal" }) => {
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(false);
  const reveal = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/admin/payout-methods/${methodId}/reveal`); setDetails(data.payout_method.details || {}); }
    catch (e) { toast.error(e?.friendlyMessage || "Révélation impossible"); }
    finally { setBusy(false); }
  };
  if (!methodId) return null;
  if (details) {
    return (
      <div className="rounded-xl p-3 text-xs space-y-0.5" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid={`${testId}-details`}>
        {Object.entries(details).filter(([, v]) => v).map(([k, v]) => <div key={k}><b className="uppercase text-[10px]">{k.replace("_", " ")}</b> : <span className="font-mono select-all">{String(v)}</span></div>)}
        <button type="button" className="underline mt-1" onClick={() => setDetails(null)} data-testid={`${testId}-hide`}><EyeOff size={11} className="inline mr-1" />Masquer</button>
      </div>
    );
  }
  return <Button size="sm" variant="outline" className="rounded-full" onClick={reveal} disabled={busy || !canManage} data-testid={`${testId}-btn`}>{busy ? <Loader2 size={13} className="animate-spin mr-1" /> : <Eye size={13} className="mr-1" />} Révéler les coordonnées</Button>;
};

const WithdrawalDetailDialog = ({ wid, canManage, onClose, onChanged }) => {
  const { config, lang } = useApp();
  const guideOn = useGuard("withdrawal_payment_guide"); // lot 759
  const appCur = config?.settings?.default_currency || "USD";
  const [det, setDet] = useState(null);
  const [action, setAction] = useState(null);
  const [note, setNote] = useState("");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/withdrawals/${wid}`).then(({ data }) => setDet(data)).catch(() => setDet(false));
  useEffect(() => {
    setDet(null); setAction(null); setNote(""); setRef("");
    if (wid) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wid]);
  const w = det?.withdrawal;
  const cur = w?.currency || appCur;
  const money = (n) => fmtMoney(n, cur, lang);
  const run = async () => {
    const meta = ACTION_META[action];
    if (meta.needsRef && !ref.trim()) { toast.error("Indique la référence du virement."); return; }
    if (meta.needsNote && note.trim().length < 3) { toast.error("Indique un motif (3 caractères minimum)."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/withdrawals/${wid}/action`, { action, note: note.trim() || null, payment_reference: ref.trim() || null });
      toast.success(`${meta.label} ✓`);
      setAction(null); setNote(""); setRef("");
      onChanged?.();
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!wid} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="withdrawal-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">Retrait {w ? money(w.amount) : ""} {w && <StatusChip status={w.status} testId="withdrawal-detail-status" />}</DialogTitle>
          <DialogDescription>{w ? `Demandé le ${fmtDate(w.requested_at, lang, true)} · réf. ${w.id.slice(0, 8).toUpperCase()}` : "Chargement…"}</DialogDescription>
        </DialogHeader>
        {det === false && <div className="text-sm py-6 text-center" style={{ color: "var(--yalola-text-muted)" }}>Retrait introuvable.</div>}
        {w && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid="withdrawal-detail-member">
                <div className="text-[10px] uppercase font-bold tracking-wide mb-1" style={{ color: "var(--yalola-text-muted)" }}>Membre</div>
                <MemberCell u={w.user} />
                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap"><KycChip status={identityOf(w.user, w.identity_status_at_request)} testId="withdrawal-detail-kyc" />
                  {w.user?.country && <span className="chip text-[10px]">{w.user.country}</span>}
                  <span className="chip text-[10px]">{det.paid_withdrawals_count} retrait(s) déjà payé(s)</span></div>
              </div>
              <div className="rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid="withdrawal-detail-wallet">
                <div className="text-[10px] uppercase font-bold tracking-wide mb-1" style={{ color: "var(--yalola-text-muted)" }}>Portefeuille du membre</div>
                <div className="text-xs space-y-0.5">
                  <div>Disponible : <b>{money(det.wallet.available)}</b></div>
                  <div>Bloqué (retraits en cours) : <b>{money(det.wallet.pending)}</b></div>
                  <div>Total reçu : {money(det.wallet.total_received)} · Total retiré : {money(det.wallet.total_withdrawn)}</div>
                </div>
              </div>
            </div>
            <div className="rounded-xl p-3 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="withdrawal-detail-payout">
              <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Bénéficiaire (coordonnées masquées)</div>
              <PayoutCell snap={det.payout_method || w.payout_snapshot} />
              {det.payout_method && !det.payout_method.legacy && (guideOn && ["approved", "processing"].includes(w.status)
                ? <WithdrawalPaymentGuide w={w} method={det.payout_method} canManage={canManage}
                                          onUseReference={(r) => { setRef(r); if (det.allowed_actions?.includes("pay")) setAction("pay"); }} />
                : <RevealDetails methodId={det.payout_method.id} canManage={canManage} />)}
              {!det.payout_method && <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Bénéficiaire supprimé par le membre — coordonnées figées : {w.payout_snapshot?.label || w.details || "—"}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Suivi</div>
              <WithdrawalTimeline history={w.history} testId="withdrawal-detail-timeline" />
              {w.available_at && ["approved", "processing"].includes(w.status) && <div className="text-[11px] mt-1" style={{ color: "var(--yalola-primary)" }} data-testid="withdrawal-detail-available">Versement prévu le {fmtDate(w.available_at, lang)}</div>}
              {w.payment_reference && <div className="text-[11px] mt-1" data-testid="withdrawal-detail-reference">Référence du virement : <b className="font-mono">{w.payment_reference}</b></div>}
            </div>
            {det.allowed_actions?.length > 0 && (
              <div className="rounded-xl p-3 space-y-2" style={{ border: "1px solid var(--yalola-border)" }} data-testid="withdrawal-detail-actions">
                <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Actions</div>
                <div className="flex gap-1.5 flex-wrap">
                  {det.allowed_actions.map((a) => {
                    const m = ACTION_META[a];
                    const cls = m.variant === "primary" ? "pill-primary rounded-full" : "rounded-full";
                    return (
                      <Button key={a} size="sm" variant={m.variant === "primary" ? "default" : "outline"} className={cls} style={m.variant === "danger" ? { color: "#B42318" } : undefined}
                        onClick={() => setAction(action === a ? null : a)} disabled={!canManage} data-testid={`withdrawal-action-${a}`}>{m.label}</Button>
                    );
                  })}
                </div>
                {action && (
                  <div className="space-y-2 pt-1" data-testid={`withdrawal-action-form-${action}`}>
                    <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{ACTION_META[action].hint}</p>
                    {ACTION_META[action].needsRef && (
                      <div><Label htmlFor="withdrawal-ref">Référence du virement *</Label><Input id="withdrawal-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Ex. VIR-2026-0001 / ID de transaction PayPal" data-testid="withdrawal-action-reference" /></div>
                    )}
                    <div><Label htmlFor="withdrawal-note">{ACTION_META[action].needsNote ? "Motif (envoyé au membre) *" : "Note (optionnelle, envoyée au membre)"}</Label>
                      <Textarea id="withdrawal-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="withdrawal-action-note" /></div>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAction(null)} disabled={busy} data-testid="withdrawal-action-cancel">Annuler</Button>
                      <Button size="sm" className="pill-primary rounded-full" onClick={run} disabled={busy} data-testid="withdrawal-action-confirm">{busy ? <Loader2 size={13} className="animate-spin mr-1" /> : <Check size={13} className="mr-1" />} Confirmer « {ACTION_META[action].label} »</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={busy} data-testid="withdrawal-detail-close">Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const WalletReconciliationCard = () => {
  const { config, lang } = useApp();
  const cur = config?.settings?.default_currency || "USD";
  const [rec, setRec] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/wallet/reconciliation").then(({ data }) => setRec(data)).catch(() => setRec(false));
  useEffect(() => { load(); }, []);
  if (rec === null || rec === false) return null;
  const ok = rec.mismatch_count === 0;
  return (
    <div className="yalola-card p-4" data-testid="wallet-reconciliation">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg grid place-items-center" style={{ background: ok ? "#D1FAE5" : "#FEE2E2", color: ok ? "#065F46" : "#991B1B" }}>{ok ? <Scale size={16} /> : <AlertTriangle size={16} />}</span>
          <div>
            <div className="font-semibold text-sm" data-testid="wallet-reconciliation-status">{ok ? "Journal et soldes cohérents" : `${rec.mismatch_count} écart(s) journal ↔ solde`}</div>
            <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{rec.checked} portefeuille(s) contrôlé(s){rec.migration?.wallet_ledger_v1 ? ` · journal migré le ${fmtDate(rec.migration.wallet_ledger_v1, lang)}` : ""}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {!ok && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpen((o) => !o)} data-testid="wallet-reconciliation-toggle">{open ? "Masquer" : "Voir les écarts"}</Button>}
          <button type="button" onClick={load} className="p-1.5 rounded-full hover:bg-black/5" title="Actualiser" data-testid="wallet-reconciliation-refresh"><RefreshCw size={14} /></button>
        </div>
      </div>
      {open && !ok && (
        <div className="mt-3 space-y-1" data-testid="wallet-reconciliation-list">
          {rec.mismatches.map((m) => (
            <div key={m.user_id} className="flex items-center justify-between text-xs rounded-lg px-2.5 py-1.5" style={{ background: "var(--yalola-surface-alt)" }}>
              <span>{m.name || m.user_id}</span>
              <span>solde {fmtMoney(m.balance, cur, lang)} · journal {fmtMoney(m.ledger, cur, lang)} · <b style={{ color: "#B42318" }}>{fmtMoney(m.diff, cur, lang)}</b></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const WithdrawalsAdmin = ({ canManage = true }) => {
  const { config, lang } = useApp();
  const [searchParams] = useSearchParams();
  const appCur = config?.settings?.default_currency || "USD";
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(searchParams.get("filter") === "overdue" ? "overdue" : "open");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [reminding, setReminding] = useState(false);
  const statusParam = status === "open" ? "requested,under_review,approved,processing" : status;
  const load = () => api.get(`/admin/withdrawals?status=${encodeURIComponent(statusParam)}${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ""}`).then(({ data: d }) => setData(d)).catch(() => setData(false));
  useEffect(() => {
    const id = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q]);
  const quick = async (w, action) => {
    try { await api.post(`/admin/withdrawals/${w.id}/action`, { action }); toast.success(`${ACTION_META[action].label} ✓`); load(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  // Renvoie tout de suite le rappel « retraits en retard » aux admins Finances (sans attendre le passage quotidien)
  const remindNow = async () => {
    setReminding(true);
    try { const { data: r } = await api.post("/admin/withdrawals/jobs/remind-overdue"); toast.success(`Rappel envoyé à ${r.staff} admin${r.staff > 1 ? "s" : ""} (${r.overdue} retrait${r.overdue > 1 ? "s" : ""} en retard)`); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setReminding(false); }
  };
  const counts = data?.counts || {};
  const openCount = ["requested", "under_review", "approved", "processing"].reduce((s, k) => s + (counts[k] || 0), 0);
  const overdueCount = data?.overdue_count || 0;
  const items = data?.withdrawals || [];
  return (
    <div className="space-y-3" data-testid="admin-withdrawals-panel">
      <WalletReconciliationCard />
      <div className="yalola-card p-4 space-y-3">
        <div className="flex gap-1.5 flex-wrap">
          {[["open", "En cours", openCount], ["overdue", "En retard", overdueCount], ...ALL_STATUSES.map((s) => [s, W_STATUS[s].fr, counts[s] || 0]), ["all", "Tous", null]].map(([k, l, n]) => (
            <button key={k} type="button" onClick={() => setStatus(k)} className={`pill text-xs ${status === k ? "pill-primary" : "pill-outline"}`}
              style={k === "overdue" && n > 0 && status !== k ? { color: "#B42318", borderColor: "#FDA29B" } : undefined} data-testid={`withdrawal-filter-${k}`} data-count={n ?? undefined}>
              {k === "overdue" && <AlertTriangle size={11} className="inline mr-1 -mt-0.5" />}{l}{n != null && <span className="ml-1 opacity-70">({n})</span>}
            </button>
          ))}
        </div>
        {data && (
          <div className="flex items-center gap-2 flex-wrap text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="withdrawal-overdue-hint" data-overdue={overdueCount}>
            <span>
              {overdueCount > 0 ? <b style={{ color: "#B42318" }}>{overdueCount} retrait{overdueCount > 1 ? "s attendent" : " attend"} une action depuis plus de {data.alert_hours} h.</b> : <>Aucun retrait n'attend une action depuis plus de {data.alert_hours} h.</>}
              {" "}Les admins Finances reçoivent un rappel chaque jour tant qu'un retrait est en retard (délai réglable dans <Link to="/admin?tab=settings&section=finance" className="underline" data-testid="withdrawal-alert-settings-link">Paramètres › Finances</Link>).
            </span>
            {overdueCount > 0 && canManage && (
              <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={remindNow} disabled={reminding} data-testid="withdrawal-remind-overdue">
                {reminding ? <Loader2 size={12} className="animate-spin mr-1" /> : <Bell size={12} className="mr-1" />} Relancer le rappel
              </Button>
            )}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-3" style={{ color: "var(--yalola-text-muted)" }} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, e-mail, référence de virement…" className="pl-8" data-testid="withdrawal-search" />
          </div>
          {data?.open_total_by_currency && Object.keys(data.open_total_by_currency).length > 0 && (
            <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="withdrawal-open-total">
              Bloqué au total : {Object.entries(data.open_total_by_currency).map(([c, v]) => fmtMoney(v, c === "?" ? appCur : c, lang)).join(" · ")}
            </div>
          )}
        </div>
      </div>
      {data === false && <div className="yalola-card p-8 text-center" style={{ color: "var(--yalola-text-muted)" }}>Impossible de charger les retraits.</div>}
      {data && items.length === 0 && <div className="yalola-card p-8 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-withdrawals-empty">Aucune demande de retrait.</div>}
      {items.length > 0 && (
        <div className="yalola-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Membre</TableHead><TableHead>Montant</TableHead><TableHead>Bénéficiaire</TableHead><TableHead>Statut</TableHead><TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((w) => (
                <TableRow key={w.id} data-testid={`withdrawal-${w.id}`} className="cursor-pointer" onClick={() => setSel(w.id)}>
                  <TableCell className="text-xs">
                    {fmtDate(w.requested_at, lang, true)}
                    {["approved", "processing"].includes(w.status) && w.available_at && <div className="text-[10px] mt-0.5" style={{ color: "var(--yalola-primary)" }} data-testid={`withdrawal-available-${w.id}`}>Versement prévu le {fmtDate(w.available_at, lang)}</div>}
                    {w.payment_reference && <div className="text-[10px] mt-0.5 font-mono" style={{ color: "var(--yalola-text-muted)" }}>{w.payment_reference}</div>}
                  </TableCell>
                  <TableCell><MemberCell u={w.user} /><div className="mt-1"><KycChip status={identityOf(w.user, w.identity_status_at_request)} /></div></TableCell>
                  <TableCell className="font-semibold whitespace-nowrap" data-testid={`withdrawal-amount-${w.id}`}>{fmtMoney(w.amount, w.currency || appCur, lang)}</TableCell>
                  <TableCell><PayoutCell snap={w.payout_snapshot || { type: w.method, label: w.details }} /></TableCell>
                  <TableCell>
                    <StatusChip status={w.status} testId={`withdrawal-status-${w.id}`} />
                    {w.overdue && <div className="mt-1"><span className="chip text-[10px]" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid={`withdrawal-overdue-${w.id}`} data-hours={Math.round(w.waiting_hours)}><AlertTriangle size={10} className="inline mr-0.5 -mt-0.5" />En retard · {Math.round(w.waiting_hours)} h</span></div>}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1.5 flex-wrap">
                      {["requested", "under_review"].includes(w.status) && <Button size="sm" className="pill-primary rounded-full" onClick={() => quick(w, "approve")} disabled={!canManage} data-testid={`withdrawal-approve-${w.id}`}><Check size={13} className="mr-1" /> Approuver</Button>}
                      {["approved", "processing"].includes(w.status) && <Button size="sm" className="pill-primary rounded-full" onClick={() => setSel(w.id)} disabled={!canManage} data-testid={`withdrawal-pay-${w.id}`}><Wallet size={13} className="mr-1" /> Marquer payé</Button>}
                      {["requested", "under_review", "approved"].includes(w.status) && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSel(w.id)} disabled={!canManage} data-testid={`withdrawal-reject-${w.id}`} style={{ color: "#B42318" }}><X size={13} /></Button>}
                      <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setSel(w.id)} data-testid={`withdrawal-open-${w.id}`}><Eye size={13} className="mr-1" /> Détail</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <WithdrawalDetailDialog wid={sel} canManage={canManage} onClose={() => setSel(null)} onChanged={load} />
    </div>
  );
};

export const BeneficiariesAdmin = ({ canManage = true }) => {
  const { lang } = useApp();
  const [rows, setRows] = useState(null);
  const [verified, setVerified] = useState("all");
  const [q, setQ] = useState("");
  const [reveal, setReveal] = useState(null);
  const load = () => api.get(`/admin/payout-methods?${verified !== "all" ? `verified=${verified}&` : ""}${q.trim() ? `q=${encodeURIComponent(q.trim())}` : ""}`).then(({ data }) => setRows(data.payout_methods || [])).catch(() => setRows(false));
  useEffect(() => {
    const id = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verified, q]);
  const toggleVerify = async (m) => {
    try { await api.post(`/admin/payout-methods/${m.id}/verify`, { verified: !m.verified_by_admin }); toast.success(m.verified_by_admin ? "Badge « vérifié » retiré" : "Bénéficiaire marqué vérifié ✓"); load(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  return (
    <div className="space-y-3" data-testid="admin-beneficiaries-panel">
      <div className="yalola-card p-4 flex items-center gap-2 flex-wrap">
        {[["all", "Tous"], ["true", "Vérifiés"], ["false", "Non vérifiés"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setVerified(k)} className={`pill text-xs ${verified === k ? "pill-primary" : "pill-outline"}`} data-testid={`beneficiary-filter-${k}`}>{l}</button>
        ))}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-3" style={{ color: "var(--yalola-text-muted)" }} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, e-mail, libellé…" className="pl-8" data-testid="beneficiary-search" />
        </div>
      </div>
      <p className="text-xs px-1" style={{ color: "var(--yalola-text-muted)" }}>Coordonnées chiffrées en base et affichées masquées. « Révéler » déchiffre les coordonnées pour exécuter un virement (action journalisée). Le badge « vérifié » est visible par le membre.</p>
      {rows === false && <div className="yalola-card p-8 text-center" style={{ color: "var(--yalola-text-muted)" }}>Impossible de charger les bénéficiaires.</div>}
      {rows && rows.length === 0 && <div className="yalola-card p-8 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-beneficiaries-empty">Aucun bénéficiaire enregistré.</div>}
      {rows && rows.length > 0 && (
        <div className="yalola-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Membre</TableHead><TableHead>Bénéficiaire</TableHead><TableHead>Utilisation</TableHead><TableHead>Ajouté le</TableHead><TableHead>Statut</TableHead><TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m.id} data-testid={`beneficiary-${m.id}`}>
                  <TableCell><MemberCell u={m.user} /><div className="mt-1"><KycChip status={identityOf(m.user)} /></div></TableCell>
                  <TableCell><PayoutCell snap={m} />{m.is_default && <span className="chip text-[10px] mt-1">Par défaut</span>}{m.legacy && <span className="chip text-[10px] mt-1 ml-1">Ancienne app</span>}</TableCell>
                  <TableCell className="text-xs">{m.paid_withdrawals || 0} retrait(s) payé(s){m.in_open_withdrawal && <div className="text-[10px]" style={{ color: "var(--yalola-primary)" }}>Retrait en cours</div>}</TableCell>
                  <TableCell className="text-xs">{fmtDate(m.created_at, lang)}</TableCell>
                  <TableCell>{m.verified_by_admin
                    ? <span className="chip text-[10px]" style={{ background: "#D1FAE5", color: "#065F46" }} data-testid={`beneficiary-verified-${m.id}`}><BadgeCheck size={10} className="inline mr-0.5" />Vérifié{m.verified_at ? ` · ${fmtDate(m.verified_at, lang)}` : ""}</span>
                    : <span className="chip text-[10px]" data-testid={`beneficiary-unverified-${m.id}`}>Non vérifié</span>}</TableCell>
                  <TableCell>
                    <div className="flex gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => toggleVerify(m)} disabled={!canManage} data-testid={`beneficiary-verify-${m.id}`}>
                        <BadgeCheck size={13} className="mr-1" /> {m.verified_by_admin ? "Retirer le badge" : "Marquer vérifié"}
                      </Button>
                      {!m.legacy && <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setReveal(m)} disabled={!canManage} data-testid={`beneficiary-reveal-${m.id}`}><Eye size={13} className="mr-1" /> Révéler</Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={!!reveal} onOpenChange={(o) => !o && setReveal(null)}>
        <DialogContent data-testid="beneficiary-reveal-modal">
          <DialogHeader>
            <DialogTitle>Coordonnées du bénéficiaire</DialogTitle>
            <DialogDescription>{reveal?.user?.name} · {reveal?.label} — l'affichage des coordonnées déchiffrées est journalisé.</DialogDescription>
          </DialogHeader>
          {reveal && <><PayoutCell snap={reveal} /><RevealDetails methodId={reveal.id} canManage={canManage} testId="beneficiary-reveal" /></>}
          <DialogFooter><Button variant="outline" onClick={() => setReveal(null)} data-testid="beneficiary-reveal-close">Fermer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
